// A request to let an app use a source. The card says who will see the data
// (the audience line, from the typed audience) and what access is asked for.
// When the owner of the connection decides, the request is routed to them and
// this card says so instead of offering buttons the person cannot use.
import { Callout } from '@/components/ui/content';
import { StatusPill } from '@/components/ui/kit';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import appApprovalsCopy, { type AppApprovalsCopy } from '@/i18n/ns/app-approvals';
import { useDecisionsApi, type AppDecisionsApi } from './app-decisions-api';
import type { AppDecision, AppGrant } from './app-decision-types';
import {
  BlockedLine,
  NoticeLine,
  OriginLine,
  UnknownBlock,
  decisionView,
  isDecidable,
  statusLabel,
  statusTone,
} from './approval-parts';
import { useAppDecision } from './use-app-decision';
import './app-cards.css';

export function audienceLine(copy: AppApprovalsCopy, grant: AppGrant): string {
  const source = grant.sourceLabel;
  switch (grant.audience.kind) {
    case 'owner_only':
      return formatCopy(copy.audienceOwner, { source });
    case 'people':
      return formatCopy(copy.audiencePeople, { count: grant.audience.count, source });
    case 'company':
      return formatCopy(copy.audienceCompany, { source });
  }
}

export function AppGrantCard({
  id,
  initial = null,
  api: explicitApi,
  onSettled,
}: {
  id: string;
  initial?: AppGrant | null;
  api?: AppDecisionsApi;
  onSettled?: (record: AppDecision) => void;
}) {
  const copy = useNamespaceCopy(appApprovalsCopy);
  const api = useDecisionsApi(explicitApi);
  const { record, loadFailed, busy, lost, stillOpen, notice, decide, checkStatus } = useAppDecision(
    api,
    'grant',
    id,
    initial,
    onSettled,
  );
  const grant = record?.kind === 'grant' ? record : null;
  if (!grant)
    return (
      <Callout tone={loadFailed ? 'danger' : 'info'}>
        {loadFailed ? copy.loadFailed : copy.loading}
      </Callout>
    );
  const view = decisionView(grant, lost);
  const decidable = isDecidable(grant, lost);
  return (
    <section className="ws-app-card" data-card="app-grant" data-view={view}>
      <header className="ws-app-card-head">
        <div>
          <h3>{formatCopy(copy.grantTitle, { app: grant.appTitle, source: grant.sourceLabel })}</h3>
          <p>
            {copy.grantWhat}: {grant.access === 'write' ? copy.grantWrite : copy.grantRead}
          </p>
          <OriginLine copy={copy} origin={grant.origin} forName={grant.requesterName} />
        </div>
        <StatusPill tone={statusTone(view)}>{statusLabel(copy, view)}</StatusPill>
      </header>
      <BlockedLine copy={copy} block={grant} />
      {view === 'unknown' && (
        <UnknownBlock
          copy={copy}
          busy={busy}
          still={stillOpen}
          onCheck={() => void checkStatus()}
        />
      )}
      <NoticeLine copy={copy} notice={notice} />
      <p className="ws-app-card-audience">{audienceLine(copy, grant)}</p>
      {view === 'pending' && !grant.canDecide && (
        <div data-routed>
          <Callout tone="info">
            {grant.ownerName
              ? formatCopy(copy.grantRouted, { owner: grant.ownerName })
              : copy.grantRoutedUnknown}
          </Callout>
        </div>
      )}
      {decidable && (
        <div className="ws-app-card-actions">
          <button
            type="button"
            className="ws-button primary"
            disabled={busy}
            onClick={() => void decide({ decision: 'approve' })}
          >
            {busy ? copy.working : copy.approve}
          </button>
          <button
            type="button"
            className="ws-button quiet"
            disabled={busy}
            onClick={() => void decide({ decision: 'reject' })}
          >
            {copy.reject}
          </button>
        </div>
      )}
    </section>
  );
}
